/** @format */

import axios from "axios";
import { API_BASE_URL } from "../../axiosConfig/apiUrl";

export const submitSendCircleInvite = async ({
	circleId,
	inviterUserId,
	inviteeEmail,
	inviteeFirstName,
	inviteeLastName,
	inviteePhone,
}: {
	circleId: string;
	inviterUserId: string;
	inviteeEmail: string;
	inviteeFirstName: string;
	inviteeLastName: string;
	inviteePhone?: string;
}) => {
	const response = await axios.post(
		`${API_BASE_URL}/circle/${circleId}/invitations`,
		{
			inviterUserId,
			inviteeEmail,
			inviteeFirstName,
			inviteeLastName,
			inviteePhone,
		},
		{ withCredentials: true },
	);

	return response.data;
};

/**
 * Fetch all circle invitations received by the current authenticated user.
 */
export const submitFetchUserInvitations = async () => {
	const response = await axios.get(`${API_BASE_URL}/invitations/user`, {
		withCredentials: true,
	});
	return response.data.invitations;
};

/**
 * Accept a circle invitation.
 */
export const submitAcceptCircleInvitation = async (
	invitationId: string,
	token: string,
) => {
	const response = await axios.post(
		`${API_BASE_URL}/invitations/${invitationId}/accept`,
		{ token },
		{ withCredentials: true },
	);
	return response.data;
};

/**
 * Deny a circle invitation.
 */
export const submitDenyCircleInvitation = async (
	invitationId: string,
	token?: string,
) => {
	const response = await axios.post(
		`${API_BASE_URL}/invitations/${invitationId}/deny`,
		{ token },
		{ withCredentials: true },
	);
	return response.data;
};

/**
 * Leader/admin review queue: member-sent invitations accepted by the invitee
 * and awaiting approval (admins: all circles, leaders: circles they lead).
 */
export const submitFetchInvitationReviewQueue = async () => {
	const response = await axios.get(
		`${API_BASE_URL}/invitations/awaiting-approval`,
		{ withCredentials: true },
	);
	return response.data.invitations;
};

/**
 * Leader/admin approves a member-sent invitation; the invitee is added to the circle.
 */
export const submitApproveCircleInvitation = async (invitationId: string) => {
	const response = await axios.post(
		`${API_BASE_URL}/invitations/${invitationId}/approve`,
		{},
		{ withCredentials: true },
	);
	return response.data;
};

/**
 * Admin only: reject an invitation at any active stage.
 */
export const submitRejectCircleInvitation = async (
	invitationId: string,
	reason?: string,
) => {
	const response = await axios.post(
		`${API_BASE_URL}/invitations/${invitationId}/reject`,
		{ reason },
		{ withCredentials: true },
	);
	return response.data;
};

export interface AdminInvitationQuery {
	page: number;
	limit: number;
	status?: string;
	circleId?: string;
	sentBy?: string;
	search?: string;
}

/**
 * Admin only: every invitation across every circle (server-side filters + pagination).
 */
export const submitFetchAdminInvitations = async (
	query: AdminInvitationQuery,
	signal?: AbortSignal,
) => {
	const params: Record<string, string | number> = {
		page: query.page,
		limit: query.limit,
	};
	if (query.status && query.status !== "ALL") params.status = query.status;
	if (query.circleId) params.circleId = query.circleId;
	if (query.sentBy && query.sentBy !== "all") params.sentBy = query.sentBy;
	if (query.search?.trim()) params.search = query.search.trim();

	const response = await axios.get(`${API_BASE_URL}/admin/invitations`, {
		params,
		withCredentials: true,
		signal,
	});
	return response.data;
};
